// src/components/TitlePicker.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import useTitlesService from "../../services/TitlesService";
import { Title, TitleSummary } from "../../types/Title";

type TitlePickerProps = {
  value: string; // current titleId (GUID or empty)
  onChange: (id: string | "", selected?: { id: string; name: string } | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  error?: string | null;
};

// Minimal shape used internally for results/selected display
type UiTitle = { id: string; name: string };

export default function TitlePicker({
  value,
  onChange,
  label = "Title",
  placeholder = "Search titles…",
  disabled,
  required,
  className,
  error,
}: TitlePickerProps) {
  const { searchTitles, getTitle } = useTitlesService();

  // Refs to keep function identities stable inside effects
  const getTitleRef = useRef(getTitle);
  const searchTitlesRef = useRef(searchTitles);

  useEffect(() => {
    getTitleRef.current = getTitle;
  }, [getTitle]);

  useEffect(() => {
    searchTitlesRef.current = searchTitles;
  }, [searchTitles]);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<UiTitle | null>(null);
  const [results, setResults] = useState<UiTitle[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const debounceMs = 300;

  // Normalize Title or TitleSummary to UiTitle
  const toUiTitle = (t: Title | TitleSummary | null | undefined): UiTitle | null =>
    t ? { id: t.id, name: t.name } : null;

  // Load current value (ID) -> display name once
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!value) {
        // Clear selection when no value
        setSelected((prev) => (prev ? null : prev));
        setQuery((prev) => (prev ? "" : prev));
        return;
      }

      try {
        const t = await getTitleRef.current(value); // returns Title
        if (!alive) return;

        const ui = toUiTitle(t);
        // Avoid redundant state updates
        setSelected((prev) => (prev?.id === ui?.id ? prev : ui));
        setQuery((prev) => (prev === (ui?.name ?? "") ? prev : ui?.name ?? ""));
      } catch {
        if (!alive) return;
        // If fetch fails, don't loop—just leave current UI state
      }
    })();

    return () => {
      alive = false;
    };
  }, [value]);

  // Debounced search when user types (but don’t search when query === selected name)
  const selectedName = selected?.name ?? "";
  useEffect(() => {
    if (!open) return;
    if (query.trim() === "") {
      setResults((prev) => (prev.length ? [] : prev));
      return;
    }
    if (selectedName && query.trim() === selectedName) {
      // User is focused but not actually changing the query relative to selection
      setResults((prev) => (prev.length ? [] : prev));
      return;
    }

    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await searchTitlesRef.current(query.trim());
        // r.items is TitleSummary[]
        const ui = (r.items ?? []).map((x) => toUiTitle(x)!) as UiTitle[];
        setResults(ui);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(handle);
  }, [query, open, selectedName]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const pick = (item: UiTitle) => {
    setSelected(item);
    setQuery(item.name);
    setResults([]);
    setOpen(false);
    onChange(item.id, item);
  };

  const clear = () => {
    if (selected || query) {
      setSelected(null);
      setQuery("");
      setResults([]);
      setOpen(false);
      onChange("");
    }
  };

  // Basic keyboard nav (Enter to pick first result, Escape to close)
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter" && open) {
      if (results.length > 0) {
        pick(results[0]);
        e.preventDefault();
      }
    }
    if (e.key === "ArrowDown") {
      setOpen(true);
    }
  };

  const hasError = !!error;
  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded text-black ${hasError ? "border-red-800" : ""}`,
    [hasError]
  );

  return (
    <div className={`w-full ${className ?? ""}`} ref={rootRef}>
      <label className="block leading-6">
        {label} {required ? "*" : ""}
      </label>

      <div className="relative">
        <input
          type="text"
          disabled={disabled}
          value={query}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onInputKeyDown}
          className={inputClass}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="titlepicker-listbox"
          aria-activedescendant=""
        />

        {query && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-sm opacity-70 hover:opacity-100"
            onClick={clear}
            disabled={disabled}
            aria-label="Clear"
          >
            ✕
          </button>
        )}

        {open && (
          <div className="absolute z-20 mt-1 w-full rounded border bg-white text-black shadow-lg max-h-64 overflow-auto">
            {loading && (
              <div className="px-3 py-2 text-sm opacity-70">Searching…</div>
            )}

            {!loading && results.length === 0 && query.trim() && (
              <div className="px-3 py-2 text-sm opacity-70">No matches</div>
            )}

            <ul id="titlepicker-listbox" role="listbox">
              {results.map((r) => (
                <li
                  key={r.id}
                  role="option"
                  aria-selected={selected?.id === r.id}
                  className={`px-3 py-2 cursor-pointer hover:bg-stone-200 ${
                    selected?.id === r.id ? "bg-stone-300" : ""
                  }`}
                  onMouseDown={(e) => e.preventDefault()} // keep focus on input
                  onClick={() => pick(r)}
                >
                  {r.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {error && (
        <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
          {error}
        </div>
      )}
    </div>
  );
}
